import { supabase } from "@/lib/supabase";

/** Genera un ticket único por backend (solo vendedores aprobados). */
export async function generateTicket(): Promise<string> {
  const { data, error } = await supabase.rpc("generate_ticket");
  if (error) throw new Error(error.message);
  return data as string;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("No se pudo leer la imagen."));
    img.src = url;
  });
}

/**
 * Quema el ticket dentro de la imagen (a nivel de píxeles) usando canvas.
 * El resultado es un archivo nuevo cuyo contenido ya incluye la marca de
 * agua, no un overlay HTML removible. Devuelve un blob WebP.
 */
export async function burnTicketIntoImage(
  file: File,
  ticket: string
): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("No se pudo procesar la imagen.");

    ctx.drawImage(img, 0, 0);

    const fontSize = Math.max(22, Math.round(img.width / 18));
    const text = ticket;
    const padding = Math.round(fontSize * 0.5);
    ctx.font = `bold ${fontSize}px Arial, sans-serif`;
    const metrics = ctx.measureText(text);
    const barW = metrics.width + padding * 2;
    const barH = fontSize + padding * 2;
    const x = padding;
    const y = img.height - padding - barH;

    // Barra semitransparente para legibilidad
    ctx.fillStyle = "rgba(0, 0, 0, 0.55)";
    ctx.fillRect(x, y, barW, barH);

    // Texto del ticket (borde + relleno)
    ctx.strokeStyle = "rgba(0, 0, 0, 0.8)";
    ctx.lineWidth = Math.max(2, Math.round(fontSize / 12));
    ctx.strokeText(text, x + padding, y + padding + fontSize);
    ctx.fillStyle = "rgba(255, 255, 255, 0.95)";
    ctx.fillText(text, x + padding, y + padding + fontSize);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/webp", 0.9)
    );
    if (!blob) throw new Error("No se pudo procesar la imagen.");
    return blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}